import { useState } from 'react';
import {
  useAddUserMutation,
  useGetUsersByDomainQuery,
  useRemoveUserMutation,
} from '@/app/Domain/api/domainsApi';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { membersColumnConfig } from './membersColumnConfig';
import { Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { errorMessage, successMessage, warningMessage } from '@/utils/toast';
import * as yup from 'yup';
import CancelModal from '@/app/common/components/CancelModal';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import IconAlert from '@/components/common/IconAlert';
import type { DomainMemberUser } from './types';

interface ManageMembersFormValues {
  phone_number: string;
  access_level: number;
}

interface ManageMembersContentProps {
  domain_slug: string | null;
}

const ManageMembersContent = ({ domain_slug }: ManageMembersContentProps) => {
  const phoneRegEx = new RegExp('^(0)?9\\d{9}$');
  const [max, setmax] = useState(11);
  const [triggeredRem, setTriggeredRem] = useState<DomainMemberUser | false>(
    false,
  );
  const [addUser] = useAddUserMutation();
  const [removeUser] = useRemoveUserMutation();
  const [agreedWithTerms, setAgreedWithTerms] = useState(false);
  const [isAgreedWithTermsError, setIsAgreedWithTermsError] = useState(false);
  const { isFetching, data: usersData } = useGetUsersByDomainQuery(
    domain_slug,
    { skip: !domain_slug },
  );

  return (
    <>
      <CancelModal
        submitTitle="آره، حذف بشه"
        desc={
          <>
            آیا از حذف کاربر{' '}
            <strong>
              {triggeredRem && `${triggeredRem.name} ${triggeredRem.family}`}
            </strong>{' '}
            مطمئن هستی؟
          </>
        }
        show={Boolean(triggeredRem)}
        title="حذف کاربر"
        setShow={setTriggeredRem}
        submitFunc={() => {
          if (!triggeredRem || !domain_slug) return;

          removeUser({
            domain_slug,
            member_id: triggeredRem.id,
          })
            .unwrap()
            .then(() => {
              successMessage('کاربر با موفقیت حذف شد');
            })
            .catch(err => {
              errorMessage('خطایی در حذف کاربر وجود داره');
              console.error(err);
            });
        }}
      />
      {!isFetching ? (
        usersData?.data?.length !== 0 ? (
          <>
            <div className="mt-3">
              <AgGridWrapper
                height="345px"
                columns={membersColumnConfig(setTriggeredRem)}
                data={usersData?.data}
              />
            </div>
            <Formik<ManageMembersFormValues>
              validationSchema={yup.object().shape({
                phone_number: yup
                  .string()
                  .required('شماره تلفن الزامی می‌باشد')
                  .matches(
                    phoneRegEx,
                    'شماره تلفن صحیح نیست، نمونه صحیح: 09121234567.',
                  ),
              })}
              onSubmit={async ({ phone_number, access_level }) => {
                if (Number(access_level) === 1 && !agreedWithTerms) {
                  setIsAgreedWithTermsError(true);
                  return;
                }

                if (!domain_slug) return;

                addUser({
                  domain_slug,
                  phone_number: phone_number.trim(),
                  access_level: Number(access_level),
                })
                  .unwrap()
                  .then(res => {
                    if (res?.success) {
                      if (res?.data?.register_required === false) {
                        successMessage('کاربر با موفقیت اضافه شد');
                      } else if (res?.data?.register_required === true) {
                        warningMessage(
                          'شماره تلفن وارد شده در حال حاضر عضوی از میزفا تولز نیست. امکان ارائه سطح دسترسی بعد از عضویت فراهم میشه.',
                        );
                      }
                    }
                  })
                  .catch(err => {
                    errorMessage('شماره وارد شده اشتباه است.');
                    console.error(err);
                  });
              }}
              initialValues={{ phone_number: '', access_level: 2 }}
            >
              {({
                values,
                errors,
                touched,
                handleBlur,
                handleSubmit,
                setFieldValue,
              }) => (
                <Form
                  onSubmit={e => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <Row className="g-2 my-3">
                    <Form.Group as={Col} sm={6}>
                      <Form.Label>شماره تلفن</Form.Label>
                      <Form.Control
                        maxLength={max}
                        placeholder=""
                        isInvalid={Boolean(
                          touched.phone_number && errors.phone_number,
                        )}
                        isValid={Boolean(
                          touched.phone_number && !errors.phone_number,
                        )}
                        value={values.phone_number}
                        name="phone_number"
                        onChange={e => {
                          if (e.target.value.startsWith('0')) setmax(11);
                          if (e.target.value.startsWith('+98')) setmax(13);
                          setFieldValue(
                            'phone_number',
                            String(e.target.value).toEnglishDigit(),
                          );
                        }}
                        onBlur={handleBlur}
                        type="text"
                      />
                      <Form.Control.Feedback
                        type={errors.phone_number ? 'invalid' : 'valid'}
                      >
                        {touched.phone_number && errors.phone_number}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} sm={3}>
                      <Form.Label>سطح دسترسی</Form.Label>
                      <Form.Select
                        placeholder=""
                        value={values.access_level}
                        name="access_level"
                        onChange={e => {
                          setFieldValue(
                            'access_level',
                            Number(e.target.value),
                          );
                        }}
                        onBlur={handleBlur}
                      >
                        <option value={1}>مالک</option>
                        <option value={2}>مدیر</option>
                        <option value={3}>دیدن</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group as={Col} sm={3}>
                      <Form.Label>&nbsp;</Form.Label>
                      <Button
                        type="submit"
                        variant="falcon-primary"
                        className="d-flex text-nowrap gap-2 align-items-center"
                      >
                        <FontAwesomeIcon icon="plus" />
                        افزودن کاربر
                      </Button>
                    </Form.Group>
                  </Row>
                  {Number(values.access_level) === 1 && (
                    <Alert
                      variant="warning"
                      className="d-flex align-items-center fs--1 p-2"
                    >
                      <Form.Check
                        isInvalid={isAgreedWithTermsError}
                        type="checkbox"
                        id="agreedToTerms"
                        className="mb-n1"
                      >
                        <Form.Check.Input
                          isInvalid={isAgreedWithTermsError}
                          type="checkbox"
                          name="agreedToTerms"
                          onChange={({ target }) => {
                            if (isAgreedWithTermsError && target.checked) {
                              setIsAgreedWithTermsError(false);
                            }
                            setAgreedWithTerms(target.checked);
                          }}
                          checked={agreedWithTerms}
                        />
                        <Form.Check.Label className="mb-0 cursor-pointer ms-1">
                          <div>
                            بعد از انتقال مالکیت، دسترسی شما به این دامنه به سطح
                            مدیر تغییر خواهد کرد.
                          </div>
                        </Form.Check.Label>
                        <Form.Control.Feedback type="invalid" className="mt-0">
                          انتقال مالکیت رو تایید نکردید!{' '}
                        </Form.Control.Feedback>
                      </Form.Check>
                    </Alert>
                  )}
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <IconAlert variant="warning">
            شما مالک این دامنه نیستید. تنها مالک دامنه اجازه دسترسی به مدیریت
            کاربران رو داره
          </IconAlert>
        )
      ) : (
        <AgGridSkeleton height="295px" />
      )}
    </>
  );
};

export default ManageMembersContent;
